import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Newspaper, Mic, FileText, ArrowUpRight, Tv, PlayCircle, Calendar, Globe } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { fetchContent } from '@/lib/contentApi';

const fallbackFeaturedArticles = [
  {
    title: "Beyond Laying Cables: Why Intelligence Will Decide Nigeria's 90,000km Fibre Project",
    source: 'The Nation',
    url: 'https://thenationonlineng.net/beyond-laying-cables-why-intelligence-will-decide-nigerias-90000km-fibre-project/',
    type: 'Opinion'
  },
  {
    title: "The Missing Link in Nigeria's 90,000km Fibre Investment",
    source: 'Leadership',
    url: 'https://leadership.ng/the-missing-link-in-nigerias-90000km-fibre-investment/',
    type: 'Analysis'
  },
  {
    title: "A Systems-Level View of Nigeria's 90,000km Fibre Investment",
    source: 'The Guardian Nigeria',
    url: 'https://guardian.ng/technology/tech/a-systems-level-view-of-nigerias-90000km-fibre-investment/',
    type: 'Feature'
  },
  {
    title: "Why Nigeria's 90,000km Fibre Rollout Must Be Intelligence-Driven",
    source: 'Vanguard',
    url: 'https://www.vanguardngr.com/2026/02/why-nigerias-90000km-fibre-rollout-must-be-intelligence%E2%80%91driven/',
    type: 'Opinion'
  },
  {
    title: 'How a Nigerian Tech Founder is Redefining Global Digital Infrastructure Intelligence',
    source: 'BusinessDay',
    url: 'https://businessday.ng/bd-weekender/article/how-a-nigerian-tech-founder-is-redefining-global-digital-infrastructure-intelligence/',
    type: 'Feature'
  },
  {
    title: 'UK-based Nexintell Ltd Unveils Infralytics-GIIS as it Enters MVP Development Phase',
    source: 'Techpoint Africa',
    url: 'https://techpoint.africa/brandpress/uk-based-nexintell-ltd-unveils-infralytics-giis-as-it-enters-mvp-development-phase/',
    type: 'Press Release'
  },
  {
    title: "Nexintell's Infralytics-GIIS Enters MVP Phase",
    source: 'TMA Street',
    url: 'https://tmastreet.com/nexintells-infralytics-giis-enters-mvp-phase/',
    type: 'News'
  },
  {
    title: 'UK-based Nexintell Ltd Unveils Infralytics-GIIS',
    source: 'OEL.TG',
    url: 'https://oel.tg/uk-based-nexintell-ltd-unveils-infralytics-giis-as-it-enters-mvp-development-phase/',
    type: 'News'
  }
];

const fallbackPublications = [
  {
    title: 'Why Digital Infrastructure Needs Intelligence, Not Just Data',
    platform: 'LinkedIn Article',
    url: 'https://www.linkedin.com/pulse/why-digital-infrastructure-needs-intelligence-just-data-attah-gchae',
    description: 'An exploration of why raw data alone is insufficient for infrastructure decision-making and how intelligence-led approaches create better outcomes.'
  },
  {
    title: 'Policy Brief',
    platform: 'LinkedIn Article',
    url: 'https://www.linkedin.com/pulse/policy-brief-ogaba-joseph-attah-sv3ze',
    description: 'Policy perspectives on digital infrastructure development and strategic recommendations for stakeholders.'
  },
  {
    title: 'Infralytics-GIIS: The Meaningful Connectivity Index Framework (MCIF) — Section 4 of the Two-Layer Infrastructure Intelligence System',
    platform: 'Zenodo · Open Access Research',
    url: 'https://doi.org/10.5281/zenodo.19052868',
    description: 'Introduces the Meaningful Connectivity Index Framework (MCIF) as Section 4 of the Infralytics-GIIS Two-Layer Infrastructure Intelligence System — a structured methodology for measuring connectivity that delivers tangible economic and social value.'
  },
  {
    title: 'Beyond Gigabit: Why UK Infrastructure Investment Needs a Structural Intelligence Framework — Evidence from the Infralytics-GIIS Index Framework, North East England',
    platform: 'Zenodo · Open Access Research',
    url: 'https://doi.org/10.5281/zenodo.19051547',
    description: 'Empirical evidence from North East England demonstrating why UK digital infrastructure investment requires a structural intelligence framework that goes beyond gigabit speed metrics, applying the Infralytics-GIIS Index methodology to regional planning.'
  }
];

const fallbackTvAppearances = [
  {
    title: 'Artificial Intelligence and Meaningful Connectivity: Leveraging AI for Optimum Productivity',
    broadcaster: 'Global TV',
    programme: 'Global Morning',
    format: 'Live broadcast — Television & Online Streaming',
    duration: 'One hour',
    date: '4th April 2026',
    reach: 'DSTV Channel 365 (54 African countries · ~22M subscribers) · StarTimes Channel 276 (30+ African countries · 15M+ subscribers) · Online Streaming',
    url: 'https://youtu.be/s0iC53BywAg?t=2756',
    images: [
      'https://customer-assets.emergentagent.com/job_9fc36052-1262-44f2-97da-869d0890939f/artifacts/en27v451_Global%20TV%20appearance%20.jpeg',
      'https://customer-assets.emergentagent.com/job_9fc36052-1262-44f2-97da-869d0890939f/artifacts/rbc65u5m_Global%20TV%20appearnce%202.jpeg'
    ]
  }
];

const socialMentions = [
  {
    platform: 'Instagram',
    url: 'https://www.instagram.com/p/DUK3HaVEtyA/',
    description: 'Nexintell infrastructure intelligence feature'
  },
  {
    platform: 'LinkedIn',
    url: 'https://www.linkedin.com/posts/techpointafrica_nexintell-ltd-a-uk-based-infrastructure-activity-7423292740701933568-mqia',
    description: 'Techpoint Africa coverage on Nexintell Ltd'
  }
];

export default function MediaPage() {
  const [featuredArticles, setFeaturedArticles] = useState(fallbackFeaturedArticles);
  const [publications, setPublications] = useState(fallbackPublications);
  const [tvAppearances, setTvAppearances] = useState(fallbackTvAppearances);

  useEffect(() => {
    fetchContent('featured_articles').then((items) => { if (items && items.length) setFeaturedArticles(items); });
    fetchContent('publications').then((items) => { if (items && items.length) setPublications(items); });
    fetchContent('tv_appearances').then((items) => { if (items && items.length) setTvAppearances(items); });
  }, []);

  return (
    <>
      <SEOHead
        title="Ogaba Joseph Attah in the Media & Publications"
        description="Independent coverage, publications, and media features on Ogaba Joseph Attah's work in digital infrastructure intelligence and technology leadership."
        canonicalUrl="https://www.ogabajosephattah.com/media"
      />
      
      <Navigation />
      
      <main className="pt-24 lg:pt-32">
        {/* Hero */}
        <section className="py-16 md:py-24" data-testid="media-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                  Press & Coverage
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] mb-6" data-testid="media-title">
                  Media & Publications
                </h1>
                <p className="text-lg text-muted-foreground">
                  Independent coverage, professional contributions, and thought leadership on digital infrastructure intelligence, technology innovation, and emerging markets.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted">
                  <img
                    src="https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/kuumaanb_One%20Community%20Team%20Headshot0170.jpg"
                    alt="Ogaba Joseph Attah - Media"
                    className="w-full h-full object-cover"
                    data-testid="media-hero-image"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="featured-articles">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-4">
                <Newspaper className="h-5 w-5 text-primary" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Featured Coverage
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                In the Press
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {featuredArticles.map((article, index) => (
                <motion.a
                  key={article.url}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group p-6 bg-card border border-border rounded-xl hover:border-primary/50 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                  data-testid={`featured-article-${index}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1 rounded-full">
                      {article.type}
                    </span>
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3 group-hover:text-primary transition-colors leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {article.source}
                  </p>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* Publications */}
        <section className="py-24 md:py-32" data-testid="publications">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-5 w-5 text-primary" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Thought Leadership
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Published Articles
              </h2>
            </motion.div>

            <div className="space-y-6">
              {publications.map((pub, index) => (
                <motion.a
                  key={pub.url}
                  href={pub.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group block p-8 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                  data-testid={`publication-${index}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">
                        {pub.platform}
                      </p>
                      <h3 className="text-xl md:text-2xl font-semibold mb-3 group-hover:text-primary transition-colors">
                        {pub.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {pub.description}
                      </p>
                    </div>
                    <ArrowUpRight className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* TV & Broadcast Appearances */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="tv-appearances">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-4">
                <Tv className="h-5 w-5 text-primary" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Television & Broadcast
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                TV Appearances
              </h2>
            </motion.div>

            <div className="space-y-10">
              {tvAppearances.map((tv, index) => (
                <motion.article
                  key={tv.url}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="overflow-hidden bg-card border border-border rounded-2xl hover:border-primary/50 hover:shadow-xl transition-all duration-300"
                  data-testid={`tv-appearance-${index}`}
                >
                  <div className="grid lg:grid-cols-2 gap-0">
                    {/* Image cluster */}
                    <a
                      href={tv.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative group block"
                      data-testid={`tv-appearance-image-link-${index}`}
                    >
                      <div className="flex flex-col gap-1 h-full bg-muted">
                        {tv.images.map((src, i) => (
                          <div key={src} className="relative overflow-hidden w-full">
                            <img
                              src={src}
                              alt={`${tv.broadcaster} appearance — ${tv.title} (${i + 1})`}
                              className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="flex items-center gap-2 text-white font-semibold bg-primary px-5 py-3 rounded-full shadow-lg">
                          <PlayCircle className="h-5 w-5" />
                          Watch Recording
                        </div>
                      </div>
                    </a>

                    {/* Details */}
                    <div className="p-8 md:p-10 flex flex-col justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                          <span className="text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1 rounded-full">
                            {tv.broadcaster}
                          </span>
                          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted px-3 py-1 rounded-full">
                            {tv.programme}
                          </span>
                        </div>
                        <h3 className="text-xl md:text-2xl font-semibold leading-tight mb-5">
                          {tv.title}
                        </h3>

                        <ul className="space-y-3 text-sm text-muted-foreground mb-6">
                          <li className="flex items-start gap-3">
                            <Calendar className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                            <span><span className="font-medium text-foreground">Broadcast Date:</span> {tv.date}</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Tv className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                            <span><span className="font-medium text-foreground">Format:</span> {tv.format} · {tv.duration}</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Globe className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                            <span><span className="font-medium text-foreground">Reach:</span> {tv.reach}</span>
                          </li>
                        </ul>
                      </div>

                      <a
                        href={tv.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-full hover:opacity-90 transition-all w-full sm:w-auto self-start"
                        data-testid={`tv-appearance-watch-${index}`}
                      >
                        <PlayCircle className="h-5 w-5" />
                        Watch on YouTube
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Social Mentions */}
        <section className="py-24 md:py-32" data-testid="social-mentions">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-4">
                <Mic className="h-5 w-5 text-primary" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Social Coverage
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Social Mentions
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {socialMentions.map((mention, index) => (
                <motion.a
                  key={mention.url}
                  href={mention.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group p-6 bg-card border border-border rounded-xl hover:border-primary/50 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`social-mention-${index}`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-semibold text-primary">
                      {mention.platform}
                    </span>
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <p className="text-muted-foreground">
                    {mention.description}
                  </p>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* Media Types */}
        <section className="py-24 md:py-32" data-testid="media-types">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-2xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                Media Engagement
              </h2>
              <p className="text-muted-foreground mb-12">
                This page features independent coverage and professional contributions including articles, interviews, podcast appearances, speaking engagements, and industry commentary.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Articles & Opinion', desc: 'Digital infrastructure intelligence insights' },
                { label: 'TV & Broadcast', desc: 'Pan-African television appearances' },
                { label: 'Interviews', desc: 'Technology & emerging markets' },
                { label: 'Speaking', desc: 'Industry events & commentary' }
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 bg-card border border-border rounded-xl text-center"
                  data-testid={`media-type-${index}`}
                >
                  <h3 className="font-semibold mb-2">{item.label}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
